/**
 * The indices of an indexed family that one diagnostic names: which, how many, and how spelled, the
 * error that refuses a negative one, and the diagnostic of a write past the family's count.
 */
import { IS_VALID_INT_RE, stringToInt } from '../godot/string/string.js';
import { ruleCount } from '../godot/variant/intSlots.js';
import { visitIndexedKeys } from '../godot/classdb/indexedKey.js';
import { armDiagnostic } from './ruleArms.js';
import { keyShapeError } from './validators/propertyError.js';
/**
 * A `*_count` has no ceiling, so one diagnostic per index can exceed the spread argument limit and
 * throw away the whole lint, and one joined message can be megabytes wide.
 */
const MAX_REPORTED_INDICES = 32;
/**
 * `0, 1, 2 and 9,997 more`: the indices a message names, capped. `total` is for a
 * caller whose `indices` is already the capped prefix.
 */
export function listIndices(indices, total = indices.length) {
    const shown = indices.slice(0, MAX_REPORTED_INDICES);
    const rest = total - shown.length;
    return rest > 0 ? `${shown.join(', ')} and ${rest.toLocaleString('en-US')} more` : shown.join(', ');
}
const positionsOf = (resolved) => typeof resolved === 'number' ? [resolved] : resolved;
/** Whether `text` spells `stored`: `+5` and `05` spell 5, and `4294967297` does not spell 1. */
function spells(text, stored) {
    return IS_VALID_INT_RE.test(text) && BigInt(text) === BigInt(stored);
}
/**
 * An index as a message names it: the text the key writes, then what Godot stores where the two
 * differ. `to_int()` lands in an `int`, so `4294967297` is stored as 1 and `x1` as 1. A nested key
 * (`0/5`) is compared position by position.
 */
export function writtenIndex(text, stored) {
    const positions = positionsOf(stored);
    const parts = text.split('/');
    const isPlain = parts.length === positions.length && parts.every((part, at) => spells(part, positions[at]));
    return isPlain ? text : `${text} (stored as ${positions.join('/')})`;
}
/**
 * The key-shape error for an index text that `to_int()` stores below zero, or null. The index is
 * read into an `int`, so `a-1` is -1 (ustring.cpp:2291-2292) and `2147483648` wraps to -2147483648.
 * `message` receives the index as {@link writtenIndex} spells it.
 */
export function negativeIndexError(indexText, key, line, message, code) {
    const index = stringToInt(indexText);
    if (index < 0)
        return keyShapeError(key, line, message(writtenIndex(indexText, index)), code);
    return null;
}
/**
 * Ascending position by position. A plain number allocates nothing, since the selection below
 * compares every entry at least once.
 */
function compareResolved(a, b) {
    if (typeof a === 'number' && typeof b === 'number')
        return a - b;
    const positionsA = positionsOf(a);
    const positionsB = positionsOf(b);
    for (let at = 0; at < Math.min(positionsA.length, positionsB.length); at++) {
        if (positionsA[at] !== positionsB[at])
            return positionsA[at] - positionsB[at];
    }
    return 0;
}
function byResolvedIndex([textA, resolvedA], [textB, resolvedB]) {
    // Several spellings can resolve alike (`5`, `+5`, `05` and `4294967301`), so the text breaks the
    // tie, shorter first, which orders plain digit runs by value.
    return (compareResolved(resolvedA, resolvedB) ||
        textA.length - textB.length ||
        (textA < textB ? -1 : textA > textB ? 1 : 0));
}
/** Where `entry` goes in the ascending `entries`: after every entry it does not precede. */
function insertionPoint(entries, entry) {
    let low = 0;
    let high = entries.length;
    while (low < high) {
        const middle = (low + high) >>> 1;
        if (byResolvedIndex(entry, entries[middle]) < 0)
            high = middle;
        else
            low = middle + 1;
    }
    return low;
}
/**
 * The {@link MAX_REPORTED_INDICES} entries a sort by {@link byResolvedIndex} puts first, in that
 * order, from one pass: a family can write 200,000 keys past its count, and a full sort orders all
 * of them to show 32. A later entry that ties goes after, where a stable sort puts it.
 */
function firstWritten(written) {
    const first = [];
    for (const entry of written) {
        const isFull = first.length === MAX_REPORTED_INDICES;
        if (isFull && byResolvedIndex(entry, first[first.length - 1]) >= 0)
            continue;
        first.splice(insertionPoint(first, entry), 0, entry);
        if (first.length > MAX_REPORTED_INDICES)
            first.pop();
    }
    return first;
}
/**
 * The index texts a message names, each as {@link writtenIndex} spells it, ascending by what each
 * resolves to and capped like {@link listIndices}. Never `Number(text)`: past 2^53 a double no
 * longer holds the integer the file states, and `Number` reads `9999999999999999999999` as `1e+22`.
 * One depth per map: `5` beside `[5, 0]` has no order, as neither position list outranks the other.
 */
export function listWrittenIndices(written) {
    // Capped before it is spelled: each `writtenIndex` parses its text twice as a BigInt.
    const shown = firstWritten(written).map(([text, resolved]) => writtenIndex(text, resolved));
    return listIndices(shown, written.size);
}
/**
 * Each index text a family's keys write at or past `count`, mapped to the index it resolves to:
 * the writes an array of that length drops. `visitIndexedKeys` visits no negative index, which is
 * the family dispatcher's report in phase 1, so one refusal is reported once.
 */
function indicesPastCount(properties, prefix, indexParse, count) {
    const past = new Map();
    visitIndexedKeys(properties, prefix, indexParse, (_key, indexText, index) => {
        if (index >= count)
            past.set(indexText, index);
    });
    return past;
}
/**
 * The writes a `PropertyListHelper` family makes past its own count, with the count they are
 * measured against. Null when the family drops nothing (it assigns past its length, or serialises
 * no count), or when the count is malformed or non-finite, which its own validator reports and no
 * message can name. An absent count is 0, the empty array the serialiser omits.
 */
function writesPastFamilyCount(properties, family) {
    const { countKey, outOfBounds } = family;
    if (countKey.key === null || outOfBounds.policy === 'assigned')
        return null;
    const count = ruleCount(properties[countKey.key]);
    if (count === null)
        return null;
    return { count, offending: indicesPastCount(properties, family.prefix, family.indexParse, count) };
}
const HELPER_DROPS_PAST_LENGTH = 'PropertyListHelper::_get_property (property_list_helper.cpp:58) returns null for an index >= the array length';
/** Why `family`'s past-count writes are lost, when its owner's own helper drops them. */
function droppedPastLength(family) {
    return (`${HELPER_DROPS_PAST_LENGTH}, so ${family.owner} never calls the matching setter and ` +
        `these ${family.prefix}<N>/… values are silently dropped on load.`);
}
/** Why `family`'s past-count writes are lost, when its owner forwards them to `popup`, whose helper drops them. */
export function droppedByPopup(family, popup) {
    return (`${family.owner} forwards the write to ${popup}, whose ${HELPER_DROPS_PAST_LENGTH}, so no setter runs ` +
        `and these ${family.prefix}<N>/… values are silently dropped on load.`);
}
/**
 * The one diagnostic of a past-count rule: the writes `family` makes on `node` past its own count,
 * reported through `arm`. `firstSentence` receives the indices as {@link listWrittenIndices} names
 * them, and the count. `dropReason` follows it. Empty when the family drops no write.
 */
export function pastCountDiagnostic(node, family, arm, firstSentence, dropReason = droppedPastLength(family)) {
    const pastCount = writesPastFamilyCount(node.rawProperties, family);
    if (pastCount === null || pastCount.offending.size === 0)
        return [];
    const indices = listWrittenIndices(pastCount.offending);
    return [armDiagnostic(arm, node, `${firstSentence(indices, pastCount.count)} ${dropReason}`)];
}
/**
 * The indices of `0..count` that `satisfied` does not hold, capped, and their
 * total, which is `count` minus the satisfied ones, so the walk stops at the cap
 * even for a legal `2147483647`. `satisfied` must hold only indices in `0..count`.
 */
export function unsatisfiedIndices(count, satisfied) {
    const listed = [];
    for (let index = 0; index < count && listed.length < MAX_REPORTED_INDICES; index++) {
        if (!satisfied.has(index))
            listed.push(index);
    }
    // Clamped: a negative `count` allocates nothing in the engine, and its own
    // validator reports it.
    return { listed, total: Math.max(0, count - satisfied.size) };
}
//# sourceMappingURL=reportedIndices.js.map